import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authService } from '../../services/auth.service';
import { useAuthStore } from '../../store/auth.store';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PasswordChangeModal } from '../../components/auth/PasswordChangeModal';
import toast from 'react-hot-toast';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const response = await authService.login(data);
      console.log('Login response:', response);
      console.log('passwordChangeRequired:', response.passwordChangeRequired);
      setUser(response);
      
      if (response.passwordChangeRequired) {
        setUserId(response.userId);
        setShowPasswordChangeModal(true);
        toast.success('Login successful! Please change your password to continue.');
      } else {
        toast.success('Login successful!');
        const isDriver = response.roles.includes('DRIVER' as any);
        navigate(isDriver ? '/trips' : '/dashboard');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChangeSuccess = async () => {
    const updatedUser = await authService.refreshUser();
    if (updatedUser) {
      setUser(updatedUser);
    }
    setShowPasswordChangeModal(false);
    const isDriver = updatedUser?.roles.includes('DRIVER' as any);
    navigate(isDriver ? '/trips' : '/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-navy to-primary flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-white rounded-2xl p-3 mb-4 shadow-md">
            <img src="/logo.png" alt="Transport ERP" className="w-16 h-16 object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-800">Harcai Transport ERP</h1>
          <p className="text-neutral-500 text-sm mt-1">Vehicle Fuel, Trip & Billing Management</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Username"
            type="text"
            placeholder="Enter your username"
            error={errors.username?.message}
            {...register('username')}
            required
          />

          <Input
            label="Password"
            type="password"
            placeholder="Enter your password"
            error={errors.password?.message}
            {...register('password')}
            required
          />

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={isLoading}
          >
            Sign In
          </Button>
        </form>

        {/*<div className="mt-6 text-center text-sm text-gray-500">*/}
        {/*  <p>Default credentials:</p>*/}
        {/*  <p className="font-mono text-xs mt-1">admin / admin123</p>*/}
        {/*</div>*/}
      </div>

      {userId && (
        <PasswordChangeModal
          isOpen={showPasswordChangeModal}
          onClose={() => {}}
          userId={userId}
          isRequired={true}
          onSuccess={handlePasswordChangeSuccess}
        />
      )}
    </div>
  );
};
